import { CustomerPagePath } from 'views/constants/enums/pageTitle';
import { AdvanceQuestionType } from './addvanceQuestion';
import { ConsentSecuritiesType } from './consentSecurities';
import { ConsentTrustType } from './consentTrust';
import { CostomerInfoType } from './costomerInfo';
import { DepositAccountType } from './depositAccount';
import { EmploymentType } from './employment';
import { InvestmentInfoType } from './investmentInfo';
import * as Yup from 'yup';
import { isEmpty } from 'lodash';
import scrollToElement from 'scroll-to-element';

//口座開設と登録情報変更でほぼ同じもの使うのでUtilで管理する

export type CommonData = { id: number; value: string };

//エラー文言
export enum ErrorWord {
  SELECT = '選択してください',
  SELECT_ONE_OR_MORE = '1つ以上を選択してください',
  INPUT = '入力してください',
  INPUT_HALF_NUMBER = '半角数字で入力してください',
  INPUT_FULL_WIDTH_CHAR = '全角で入力してください',
  INPUT_FULL_WIDTH_KATAKANA = '全角カナで入力してください',
  UABLE_OPEN_ACCOUNT = '未同意の場合は口座開設できません',
  UABLE_OPEN_ACCOUNT_TYPE2 = '上記項目に該当する場合は、✓を入力してください（該当しない場合は、口座開設できません）',
  UABLE_OPEN_ACCOUNT_TYPE3 = '外国PEPsに該当しない場合は、✓を入力してください（該当する場合は、口座開設できません）',
  UABLE_OPEN_ACCOUNT_TYPE4 = 'チェックの無い場合は口座開設できません',
  UABLE_OPEN_ACCOUNT_TYPE5 = '承諾の無い場合は口座開設できません',
  POSTCODE = '7桁の半角数字で入力してください（ハイフン不要）（例: 1234567）',
  MOBILE_PHONE = '11桁の半角数字で入力してください（ハイフン不要）（例: 09012345678）',
  HOME_PHONE = '10桁の半角数字で入力してください（ハイフン不要）（例: 0312345678）',
  EMAIL = 'メールアドレスの形式で入力してください（例: example@example.com）',
  Date = '有効な日付を入力してください',
  SSN = '9桁の半角数字で入力してください',
  ITIN = '9桁の半角数字で入力してください',
  MANAGET_CODE = '6桁の文字で入力してください',
  PHONE_NUMBER = '10～11桁の半角数字で入力してください',
  BANK_INFO = '金融機関を検索してください',
}

//ルートパス
export enum EstablishPageType {
  Login = CustomerPagePath.Login,
  AddvanceQuestion = CustomerPagePath.AddvanceQuestion,
  ConsentSecurities = CustomerPagePath.ConsentSecurities,
  ConsentTrust = CustomerPagePath.ConsentTrust,
  CustomerInfo = CustomerPagePath.CustomerInfo,
  DepositAccount = CustomerPagePath.DepositAccount,
  Employment = CustomerPagePath.Employment,
  InvestmentInfo = CustomerPagePath.InvestmentInfo,
  RequestConfirm = CustomerPagePath.RequestConfirm,
  RequestComplete = CustomerPagePath.RequestComplete,
}

/*--------------
  口座開設全体の型
  --------------*/
export type AccountEstablishRequestParam = AdvanceQuestionType &
  ConsentSecuritiesType &
  ConsentTrustType &
  CostomerInfoType &
  DepositAccountType &
  EmploymentType &
  InvestmentInfoType;

/*--------------
  バリデーション
  --------------*/
//はい・いいえのラジオボタン条件分岐
export const yapBoolWhen = (target: string, bool: boolean, errorWord: string) =>
  Yup.bool().when(target, {
    is: bool,
    then: () => Yup.bool().nonNullable(errorWord),
    otherwise: (schema) => schema.strip(),
  });
//チェックボックス必須の条件分岐
export const yapCheckRequiredWhen = (target: string, bool: boolean, errorWord: string) =>
  Yup.bool().when(target, {
    is: bool,
    then: (schema) => schema.required(errorWord).oneOf([true], errorWord),
    otherwise: (schema) => schema.strip(),
  });
//文字列必須の条件分岐
export const yapStringWhen = (target: string, bool: boolean, errorWord: string) =>
  Yup.string().when(target, {
    is: bool,
    then: () => Yup.string().required(errorWord),
    otherwise: (schema) => schema.strip(),
  });
export const yapStringWithLengthWhen = (target: string, bool: boolean, length: number, errorWord: string) =>
  Yup.string().when(target, {
    is: bool,
    then: () => Yup.string().required(errorWord).length(length, errorWord),
    otherwise: (schema) => schema.strip(),
  });

const createLengthErrorWord = (length?: number) => {
  if (!length) {
    return '';
  } else {
    return `${length}桁の`;
  }
};

type YupNumberStringParam = {
  length: number;
  isRequired: boolean;
  errorWord: string;
};
export const yupNumberString = ({ length, isRequired, errorWord }: YupNumberStringParam) => {
  let schema = isRequired ? Yup.string().required(errorWord) : Yup.string().nullable();
  schema = schema.length(length, errorWord).matches(/[0-9]+/gi, errorWord);

  return schema;
};

//数値必須の条件分岐
export const yapNumberWhen = (
  target: string,
  bool: boolean,
  errorWord: string,
  length?: number,
  isAddLengthErrorWord: boolean = true,
) => {
  if (length) {
    const lengthErrorWord = isAddLengthErrorWord ? `${createLengthErrorWord(length)}${errorWord}` : errorWord;
    return Yup.number().when(target, {
      is: bool,
      then: () => yupNumberString({ length, isRequired: true, errorWord: lengthErrorWord }),
      otherwise: (schema) => schema.strip(),
    });
  } else {
    return Yup.number().when(target, {
      is: bool,
      then: () => Yup.number().required(errorWord).typeError(errorWord),
      otherwise: (schema) => schema.strip(),
    });
  }
};

/*--------------
  スクロールの振る舞い※ライブラリ：scrollToElement
  --------------*/
const elemScroll = (formErrorElem: HTMLCollectionOf<Element>) => {
  if (isEmpty(formErrorElem)) return;
  scrollToElement(formErrorElem[0], {
    offset: -70,
    ease: 'in-out-quad',
    duration: 800,
  });
};
/*--------------
  バリデーションチェックでエラーになった際のスクロール
  --------------*/
//className
const formErrorClassName = 'formError';
//スクロールの実装。エラーになっている要素に一番上にスクロール。useEffectでformikのエラーあれば使う感じ
export const formErrorScroll = () => {
  const formErrorElem = document.getElementsByClassName(formErrorClassName);
  elemScroll(formErrorElem);
};
//スクロール先の要素。エラーなら表示するように実装する
export const FormErrorScrollTarget = () => <div className={formErrorClassName} />;

/*--------------
  画面のTOPへのスクロール
  --------------*/
export const scrollToTop = () => {
  const formErrorElem = document.getElementsByClassName('body');
  elemScroll(formErrorElem);
};
